import { alpha, BoxProps, keyframes, Skeleton, SkeletonProps, Theme, useTheme } from "@mui/material";

import { FormTypographyProps } from "@/forms-design-system/FormTypography";
import { mergeSx } from "@/helpers/mergeSx";

export type DashboardSkeletonProps = SkeletonProps & {
  maxWidth?: BoxProps["maxWidth"];
  minWidth?: BoxProps["minWidth"];
  fontSize?: FormTypographyProps["variant"];
};

export const DashboardSkeleton = ({ sx, maxWidth, minWidth, fontSize, ...rest }: DashboardSkeletonProps) => {
  const theme = useTheme();

  const gotFontProperties = () => {
    if (!fontSize || !(fontSize in theme.typography)) {
      return {};
    }
    const typographyVariant = theme.typography[fontSize as keyof typeof theme.typography];
    return typeof typographyVariant === "object" && "fontSize" in typographyVariant
      ? {
          fontSize: typographyVariant.fontSize,
          lineHeight: typographyVariant.lineHeight,
        }
      : {};
  };

  const styles = mergeSx(
    {
      maxWidth,
      minWidth,
      ...gotFontProperties(),
    },
    sx
  );

  return <Skeleton sx={styles} {...rest} />;
};

// Use this if you have a complex svg element that you need to pulse like a DashboardSkeleton
export const getSkeletonColor = (theme: Theme) => alpha(theme.palette.text.primary, 0.11);

const skeletonPulseAnimationName = keyframes`
0%, 100% { opacity: 1; }
50% { opacity: 0.4; }
`;

export const skeletonPulseAnimation = `${skeletonPulseAnimationName} 2000ms ease-in-out 500ms infinite`;
